import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { useEffect, useMemo, useState } from 'react'
import { apiUpload } from '../../../helpers/apiRequester'
import { fetchApprovedLenders } from '../../../helpers/approvedLenders'
import Modal from '../../reusable/modal'
import { InputFieldAddressLookup } from '../../reusable/inputFieldAddressLookup'
import { performClientSearch } from '../subViews/unifiedSearchViews/shared/clientSearchService'
import { ClientData } from '../subViews/unifiedSearchViews/shared/types'
import styles from './styles.module.scss'

interface ManualClientForm {
    title: string
    first_name: string
    last_name: string
    dob: string
    email: string
    phone: string
    building_no_street: string
    building: string
    town_area: string
    city: string
    postcode: string
    lender: string
    policy_number: string
    vehicle_make: string
    vehicle_model: string
    vehicle_registration: string
    apr: string
    credit_amount: string
    finance_amount: string
}

const emptyForm: ManualClientForm = {
    title: '',
    first_name: '',
    last_name: '',
    dob: '',
    email: '',
    phone: '',
    building_no_street: '',
    building: '',
    town_area: '',
    city: '',
    postcode: '',
    lender: '',
    policy_number: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_registration: '',
    apr: '',
    credit_amount: '',
    finance_amount: '',
}

interface CreateResult {
    link: string
    reused: boolean
    sent: { email: boolean; sms: boolean }
    noClaim: boolean
    emailSent: boolean
    emailRequested: boolean
}

// The date input needs YYYY-MM-DD; search results can arrive in several formats.
const normaliseDob = (dob?: string): string => {
    if (!dob) return ''
    const isoMatch = dob.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`
    const ukMatch = dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (ukMatch) return `${ukMatch[3]}-${ukMatch[2]}-${ukMatch[1]}`
    const parsed = new Date(dob)
    if (!isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0]
    return ''
}

const getMaxDob = () => {
    const today = new Date()
    return new Date(today.getFullYear() - 18, today.getMonth(), today.getDate()).toISOString().split('T')[0]
}

export const ManualAddClientView = () => {
    const [searchEmail, setSearchEmail] = useState('')
    const [searchPhone, setSearchPhone] = useState('')
    const [searchResults, setSearchResults] = useState<ClientData[]>([])
    const [prefillNote, setPrefillNote] = useState('')

    const [form, setForm] = useState<ManualClientForm>(emptyForm)
    const [addressSelected, setAddressSelected] = useState(false)
    const [financeAgreementFile, setFinanceAgreementFile] = useState<File | null>(null)
    const [sendEmail, setSendEmail] = useState(false)
    const [sendSms, setSendSms] = useState(false)

    const [createResult, setCreateResult] = useState(false)
    const [result, setResult] = useState<CreateResult | null>(null)
    const [lenderOptions, setLenderOptions] = useState<string[]>([])
    const [lenderListOpen, setLenderListOpen] = useState(false)
    const [lendersLoaded, setLendersLoaded] = useState(false)
    const [acknowledgedUnapproved, setAcknowledgedUnapproved] = useState(false)
    // Explicit, default-off opt-in for the no-claim email — never auto-sent.
    const [sendNoClaimEmail, setSendNoClaimEmail] = useState(false)

    useEffect(() => {
        const fetchLenders = async () => {
            try {
                // Centralised approved-lender list (active funder_defendants).
                const lenders = await fetchApprovedLenders()
                setLenderOptions(lenders.map((lender) => lender.defendant))
            } catch (error) {
                console.error('Unable to fetch lenders:', error)
                Report.failure('Lenders unavailable', 'Could not load the lender list.', 'Okay')
            } finally {
                setLendersLoaded(true)
            }
        }

        fetchLenders()
    }, [])

    const trimmedLender = form.lender.trim()
    const lenderInList = useMemo(
        () => lenderOptions.some((option) => option.toLowerCase() === trimmedLender.toLowerCase()),
        [lenderOptions, trimmedLender]
    )
    // A lender that isn't one of the funder-monitoring defendants has no claim we
    // can pursue, so it routes into the no-claim acknowledgement + email flow.
    const lenderUnapproved = Boolean(trimmedLender) && !lenderInList
    const submitDisabled = lenderUnapproved && !acknowledgedUnapproved

    const filteredLenders = useMemo(() => {
        const query = trimmedLender.toLowerCase()
        const matches = query
            ? lenderOptions.filter((option) => option.toLowerCase().includes(query))
            : lenderOptions
        return matches.slice(0, 8)
    }, [lenderOptions, trimmedLender])

    // Randomised field name so Chrome/Safari can't match a previously-saved
    // "lender" value and silently autofill it over what the user typed.
    const lenderFieldName = useMemo(() => `lender-${Math.random().toString(36).slice(2)}`, [])

    // Changing the lender invalidates a previous unapproved-lender acknowledgement
    // and any no-claim email opt-in.
    const setLenderValue = (value: string) => {
        setAcknowledgedUnapproved(false)
        setSendNoClaimEmail(false)
        setForm((prev) => ({ ...prev, lender: value }))
    }

    const selectLender = (value: string) => {
        setLenderValue(value)
        setLenderListOpen(false)
    }

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setForm((prev) => ({ ...prev, [name]: value }))
    }

    const handleAddressSelection = ({ field, value }: { field: string; value: any }) => {
        if (field === 'address') {
            const arr = String(value).split(',')
            const line1 = arr[0]?.trim() || ''
            const line2 = arr.length > 4 ? arr[1]?.trim() || '' : ''
            const town = arr.length > 3 ? arr[arr.length - 3]?.trim() || '' : ''
            const cityVal = arr[arr.length - 2]?.trim() || ''
            const pc = arr[arr.length - 1]?.trim() || ''
            setForm((prev) => ({
                ...prev,
                building_no_street: line1,
                building: line2,
                town_area: town,
                city: cityVal,
                postcode: pc,
            }))
            setAddressSelected(true)
        }
        return { success: true, error: '' }
    }

    const applyPrefill = (client: ClientData) => {
        const addr = client.current_address || {}
        const line1 = [addr.flat, addr.house_name, addr.house_number, addr.street].filter(Boolean).join(' ').trim()
        setForm({
            ...emptyForm,
            first_name: client.firstname || '',
            last_name: client.lastname || '',
            dob: normaliseDob(client.dob),
            email: client.email || '',
            phone: client.telephone || '',
            building_no_street: line1,
            building: addr.county || '',
            town_area: addr.town_city || '',
            city: addr.city || addr.town_city || '',
            postcode: addr.postcode || '',
        })
        setAddressSelected(Boolean(line1 || addr.postcode))
        setSearchResults([])
        setPrefillNote(`Prefilled from an existing client in ${client.origin}. Review the details and add the missing documents before submitting.`)
        Report.success('Client found', `Prefilled details from ${client.origin}.`, 'Okay')
    }

    const handleSearch = async () => {
        if (!searchEmail.trim() && !searchPhone.trim()) {
            Report.info('Search', 'Enter an email address or phone number to search.', 'Okay')
            return
        }
        Loading.circle('Searching for existing clients...')
        const searchResult = await performClientSearch({ email: searchEmail, phone: searchPhone })
        Loading.remove()

        if (!searchResult.success) {
            Report.failure(
                'Search failed',
                typeof searchResult.errors === 'string' ? searchResult.errors : 'Could not complete the search.',
                'Okay'
            )
            return
        }
        if (searchResult.totalResults === 0 || searchResult.results.length === 0) {
            Report.info('No results', 'No existing clients matched your search. You can still add them manually below.', 'Okay')
            setSearchResults([])
            return
        }
        if (searchResult.results.length === 1) {
            applyPrefill(searchResult.results[0])
            return
        }
        setSearchResults(searchResult.results)
    }

    const clearSearch = () => {
        setSearchEmail('')
        setSearchPhone('')
        setSearchResults([])
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null
        setFinanceAgreementFile(file)
    }

    const closeModal = () => {
        setCreateResult(false)
        setResult(null)
    }

    const copyLink = async () => {
        if (!result?.link) return
        try {
            await navigator.clipboard.writeText(result.link)
            Report.success('Copied', 'The signing link has been copied to your clipboard.', 'Okay')
        } catch {
            Report.failure('Copy failed', 'Could not copy the link. Please copy it manually.', 'Okay')
        }
    }

    const resetForm = () => {
        setForm(emptyForm)
        setAddressSelected(false)
        setFinanceAgreementFile(null)
        setSendEmail(false)
        setSendSms(false)
        setAcknowledgedUnapproved(false)
        setSendNoClaimEmail(false)
        setPrefillNote('')
        setSearchEmail('')
        setSearchPhone('')
        setSearchResults([])
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (!form.first_name.trim() || !form.last_name.trim()) {
            Report.failure('Error', 'First name and last name are required.', 'Okay')
            return
        }
        if (!form.email.trim()) {
            Report.failure('Error', 'Email address is required.', 'Okay')
            return
        }
        if (!financeAgreementFile) {
            Report.failure('Error', 'Please upload the proof of finance agreement.', 'Okay')
            return
        }
        if (lenderUnapproved && !acknowledgedUnapproved) {
            Report.failure('Unapproved lender', 'Please acknowledge the no-claim notice before continuing.', 'Okay')
            return
        }

        Loading.circle('Adding client...')
        try {
            const fd = new FormData()
            Object.entries(form).forEach(([key, value]) => fd.append(key, value ?? ''))
            fd.append('sendEmail', String(sendEmail))
            fd.append('sendSms', String(sendSms))
            fd.append('acknowledgedUnapproved', String(acknowledgedUnapproved))
            fd.append('sendNoClaimEmail', String(sendNoClaimEmail))
            fd.append('financeAgreement', financeAgreementFile)

            const apiResult = await apiUpload('manual-add-client', fd)
            Loading.remove()

            if (!apiResult.data?.success) {
                return Report.failure('Error', apiResult.data?.message || 'An error occurred.', 'Okay')
            }

            setResult({
                link: apiResult.data.signing_link,
                reused: Boolean(apiResult.data.reused),
                sent: apiResult.data.sent || { email: sendEmail, sms: sendSms },
                noClaim: Boolean(apiResult.data.noClaim),
                emailSent: Boolean(apiResult.data.emailSent),
                emailRequested: Boolean(apiResult.data.emailRequested),
            })
            setCreateResult(true)
            resetForm()
        } catch (error: any) {
            Loading.remove()
            const message = error?.response?.data?.message || 'An error occurred while adding the client.'
            Report.failure('Error', message, 'Okay')
        }
    }

    return (
        <div className={styles.clientView}>
            <section className={styles.searchPanel}>
                <h4>Search existing clients</h4>
                <p className={styles.searchHint}>
                    Check whether the client already exists (e.g. missing a finance agreement). Search by email or phone
                    to prefill their details.
                </p>
                <div className={styles.searchRow}>
                    <input
                        className={styles.textInput}
                        type="email"
                        placeholder="Email address"
                        value={searchEmail}
                        onChange={(e) => setSearchEmail(e.target.value)}
                    />
                    <input
                        className={styles.textInput}
                        type="tel"
                        placeholder="Phone number"
                        value={searchPhone}
                        onChange={(e) => setSearchPhone(e.target.value)}
                    />
                    <button type="button" className={styles.searchButton} onClick={handleSearch}>
                        Search
                    </button>
                    <button type="button" className={styles.clearButton} onClick={clearSearch}>
                        Clear
                    </button>
                </div>

                {searchResults.length > 0 && (
                    <div className={styles.searchResults}>
                        <p className={styles.resultsCount}>{searchResults.length} matching clients found:</p>
                        {searchResults.map((client, index) => (
                            <div key={`${client.client_id}-${index}`} className={styles.resultCard}>
                                <div className={styles.resultInfo}>
                                    <strong>
                                        {client.firstname} {client.lastname}
                                    </strong>
                                    <span>{client.email}</span>
                                    <span>{client.telephone}</span>
                                    <span className={styles.resultOrigin}>{client.origin}</span>
                                </div>
                                <button type="button" className={styles.useButton} onClick={() => applyPrefill(client)}>
                                    Use this client
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {prefillNote && <div className={styles.prefillNote}>{prefillNote}</div>}

            <form onSubmit={handleSubmit}>
                <fieldset>
                    <label>First Name</label>
                    <input
                        className={styles.textInput}
                        name="first_name"
                        value={form.first_name}
                        onChange={onChange}
                        type="text"
                        placeholder="Enter first name"
                        required
                    />
                </fieldset>
                <fieldset>
                    <label>Last Name</label>
                    <input
                        className={styles.textInput}
                        name="last_name"
                        value={form.last_name}
                        onChange={onChange}
                        type="text"
                        placeholder="Enter last name"
                        required
                    />
                </fieldset>
                <fieldset>
                    <label>Title</label>
                    <select className={styles.textInput} name="title" value={form.title} onChange={onChange}>
                        <option value="">Select title</option>
                        <option value="Mr">Mr</option>
                        <option value="Mrs">Mrs</option>
                        <option value="Ms">Ms</option>
                        <option value="Miss">Miss</option>
                        <option value="Dr">Dr</option>
                    </select>
                </fieldset>
                <fieldset>
                    <label>Date of Birth</label>
                    <input
                        className={styles.textInput}
                        name="dob"
                        value={form.dob}
                        onChange={onChange}
                        type="date"
                        max={getMaxDob()}
                    />
                </fieldset>
                <fieldset>
                    <label>Email Address</label>
                    <input
                        className={styles.textInput}
                        name="email"
                        value={form.email}
                        onChange={onChange}
                        type="email"
                        placeholder="Enter email address"
                        required
                    />
                </fieldset>
                <fieldset>
                    <label>Telephone Number</label>
                    <input
                        className={styles.textInput}
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        type="tel"
                        placeholder="Enter phone number"
                    />
                </fieldset>

                <div className={styles.formDiv}>
                    <h4>Client Address</h4>
                    <fieldset>
                        <label>Search Address</label>
                        <InputFieldAddressLookup
                            fieldName="searchAddress"
                            saveValue={handleAddressSelection}
                            enhanced={true}
                        />
                    </fieldset>
                    {addressSelected && (
                        <div className={styles.addressGrid}>
                            <fieldset>
                                <label>Building No / Street</label>
                                <input
                                    className={styles.textInput}
                                    name="building_no_street"
                                    value={form.building_no_street}
                                    onChange={onChange}
                                    type="text"
                                    placeholder="Building number and street"
                                />
                            </fieldset>
                            <fieldset>
                                <label>Building / Address Line 2</label>
                                <input
                                    className={styles.textInput}
                                    name="building"
                                    value={form.building}
                                    onChange={onChange}
                                    type="text"
                                    placeholder="Building or additional line"
                                />
                            </fieldset>
                            <fieldset>
                                <label>Town / Area</label>
                                <input
                                    className={styles.textInput}
                                    name="town_area"
                                    value={form.town_area}
                                    onChange={onChange}
                                    type="text"
                                    placeholder="Town or area"
                                />
                            </fieldset>
                            <fieldset>
                                <label>City</label>
                                <input
                                    className={styles.textInput}
                                    name="city"
                                    value={form.city}
                                    onChange={onChange}
                                    type="text"
                                    placeholder="City"
                                />
                            </fieldset>
                            <fieldset>
                                <label>Postcode</label>
                                <input
                                    className={styles.textInput}
                                    name="postcode"
                                    value={form.postcode}
                                    onChange={onChange}
                                    type="text"
                                    placeholder="Postcode"
                                />
                            </fieldset>
                        </div>
                    )}
                </div>

                <div className={styles.formDiv}>
                    <h4>Claim / Finance Details</h4>
                    <div className={styles.addressGrid}>
                        <fieldset>
                            <label>Lender</label>
                            <div className={styles.lenderField}>
                                <input
                                    className={styles.textInput}
                                    name={lenderFieldName}
                                    value={form.lender}
                                    onChange={(e) => setLenderValue(e.target.value)}
                                    onFocus={() => setLenderListOpen(true)}
                                    onBlur={() => window.setTimeout(() => setLenderListOpen(false), 120)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Escape') setLenderListOpen(false)
                                        if (e.key === 'Enter') {
                                            e.preventDefault()
                                            setLenderListOpen(false)
                                        }
                                    }}
                                    type="text"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck={false}
                                    data-lpignore="true"
                                    data-1p-ignore
                                    role="combobox"
                                    aria-autocomplete="list"
                                    aria-expanded={lenderListOpen}
                                    aria-controls={`${lenderFieldName}-list`}
                                    placeholder={lendersLoaded ? 'Search or type a lender' : 'Loading lenders...'}
                                />
                                {lenderListOpen && filteredLenders.length > 0 && (
                                    <ul className={styles.lenderDropdown} id={`${lenderFieldName}-list`}>
                                        {filteredLenders.map((lender) => (
                                            <li key={lender}>
                                                <button
                                                    type="button"
                                                    className={styles.lenderOption}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault()
                                                        selectLender(lender)
                                                    }}
                                                >
                                                    {lender}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            {lenderUnapproved ? (
                                <span className={styles.lenderNotice}>
                                    Not on the approved list — will be recorded as a no-claim.
                                </span>
                            ) : (
                                <span className={styles.lenderHint}>
                                    Pick from the list, or type a lender that isn't listed.
                                </span>
                            )}
                        </fieldset>
                        <fieldset>
                            <label>Policy / Agreement Number</label>
                            <input
                                className={styles.textInput}
                                name="policy_number"
                                value={form.policy_number}
                                onChange={onChange}
                                type="text"
                                placeholder="Policy or agreement number"
                            />
                        </fieldset>
                        <fieldset>
                            <label>Vehicle Make</label>
                            <input
                                className={styles.textInput}
                                name="vehicle_make"
                                value={form.vehicle_make}
                                onChange={onChange}
                                type="text"
                                placeholder="Vehicle make"
                            />
                        </fieldset>
                        <fieldset>
                            <label>Vehicle Model</label>
                            <input
                                className={styles.textInput}
                                name="vehicle_model"
                                value={form.vehicle_model}
                                onChange={onChange}
                                type="text"
                                placeholder="Vehicle model"
                            />
                        </fieldset>
                        <fieldset>
                            <label>Vehicle Registration</label>
                            <input
                                className={styles.textInput}
                                name="vehicle_registration"
                                value={form.vehicle_registration}
                                onChange={onChange}
                                type="text"
                                placeholder="Vehicle registration"
                            />
                        </fieldset>
                        <fieldset>
                            <label>APR (%)</label>
                            <input
                                className={styles.textInput}
                                name="apr"
                                value={form.apr}
                                onChange={onChange}
                                type="text"
                                placeholder="APR"
                            />
                        </fieldset>
                        <fieldset>
                            <label>Credit Amount</label>
                            <input
                                className={styles.textInput}
                                name="credit_amount"
                                value={form.credit_amount}
                                onChange={onChange}
                                type="text"
                                placeholder="Credit amount"
                            />
                        </fieldset>
                        <fieldset>
                            <label>Finance Amount</label>
                            <input
                                className={styles.textInput}
                                name="finance_amount"
                                value={form.finance_amount}
                                onChange={onChange}
                                type="text"
                                placeholder="Finance amount"
                            />
                        </fieldset>
                    </div>
                </div>

                <div className={styles.formDiv}>
                    <h4>Proof of Finance Agreement</h4>
                    <p className={styles.uploadHint}>Upload the finance agreement document (PDF or image). Required.</p>
                    <label className={styles.fileUpload}>
                        <input type="file" accept="application/pdf,image/*" onChange={handleFileChange} />
                        <span className={styles.fileUploadLabel}>
                            {financeAgreementFile ? financeAgreementFile.name : 'Choose file...'}
                        </span>
                    </label>
                </div>

                <div className={styles.formDiv}>
                    <h4>Signing Link Delivery</h4>
                    <p className={styles.uploadHint}>
                        A CFA signing link is always generated and shown after submitting. Optionally also send it to the
                        client.
                    </p>
                    <div className={styles.deliveryOptions}>
                        <label className={styles.checkboxLabel}>
                            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                            Email link to client
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} />
                            SMS link to client
                        </label>
                    </div>
                </div>

                {lenderUnapproved && (
                    <div className={styles.unapprovedBox}>
                        <h4>Unapproved Lender</h4>
                        <p>
                            The lender you entered is not on our approved list, so we do not have a claim we can pursue
                            for this client. You can still proceed (the client is recorded as no&#8209;claim, no CFA link
                            is generated). No email is sent unless you explicitly opt in below.
                        </p>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={acknowledgedUnapproved}
                                onChange={(e) => setAcknowledgedUnapproved(e.target.checked)}
                            />
                            I confirm this lender is not approved and want to proceed (required)
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={sendNoClaimEmail}
                                disabled={!acknowledgedUnapproved}
                                onChange={(e) => setSendNoClaimEmail(e.target.checked)}
                            />
                            Also email the client to confirm we have no claim for them (optional)
                        </label>
                    </div>
                )}

                <button type="submit" disabled={submitDisabled} className={submitDisabled ? styles.submitDisabled : ''}>
                    <span>
                        {lenderUnapproved
                            ? sendNoClaimEmail
                                ? 'Add Client (No Claim) & Send Email'
                                : 'Add Client (No Claim)'
                            : 'Add Client & Generate CFA Link'}
                    </span>
                </button>
            </form>

            <Modal modalState={createResult} previewModalAction={closeModal}>
                <div className={styles.resultModal}>
                    {result?.noClaim ? (
                        <>
                            <h2>Client added &mdash; no claim</h2>
                            <p>
                                The selected lender is not approved, so no CFA signing link was generated. The client
                                has been recorded as having no claim.
                            </p>
                            <div className={styles.linkSection}>
                                <h3>No-Claim Email</h3>
                                <p>
                                    {result?.emailSent
                                        ? "We've emailed the client to confirm we don't have a claim for them."
                                        : result?.emailRequested
                                        ? 'The no-claim email could not be sent. Please contact the client manually to let them know we have no claim for them.'
                                        : 'No email was sent (you did not opt in). The client has only been recorded as no-claim — contact them manually if needed.'}
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            <h2>Client {result?.reused ? 'updated' : 'created'} successfully</h2>
                            <p>
                                {result?.reused
                                    ? 'Matched an existing ZappedCases client and added a new case + finance agreement.'
                                    : 'A new ZappedCases client and case were created with the finance agreement on file.'}
                            </p>

                            <div className={styles.linkSection}>
                                <h3>CFA Signing Link</h3>
                                <p>Share this link with the client so they can sign their CFA:</p>
                                <div className={styles.linkRow}>
                                    <a href={result?.link} target="_blank" rel="noopener noreferrer">
                                        {result?.link}
                                    </a>
                                    <button type="button" onClick={copyLink}>
                                        Copy
                                    </button>
                                </div>
                            </div>

                            {(result?.sent.email || result?.sent.sms) && (
                                <p className={styles.sentNote}>
                                    Link sent to client via {result?.sent.email ? 'email' : ''}
                                    {result?.sent.email && result?.sent.sms ? ' and ' : ''}
                                    {result?.sent.sms ? 'SMS' : ''}.
                                </p>
                            )}
                        </>
                    )}
                </div>
            </Modal>
        </div>
    )
}
